/**
 * Client-side image compressor for audit-photo uploads.
 *
 * Photos taken on modern phones routinely run 4–8 MB at 4000×3000 px;
 * a single audit easily reaches the multi-megabyte territory when
 * uploaded in bulk. Re-encoding to JPEG at ~85% quality with a
 * 1920 px long-edge cap keeps the photo perfectly legible for audit
 * evidence while typically cutting size 5–10×.
 *
 * Pure browser APIs (`<img>` + `<canvas>` + `canvas.toBlob`) — no
 * extra dependency. PNG inputs become JPEG outputs because they
 * compress far better at the resolutions we care about.
 */

export interface CompressOptions {
  /** Largest edge in pixels. The image is scaled proportionally to
   *  fit. Defaults to 2560 — closer to the native 12 MP phone-camera
   *  resolution than Full-HD, so audit evidence keeps enough detail
   *  to zoom into a leaf or a label. */
  maxDim?: number;
  /** JPEG quality 0..1. Defaults to 0.92 (very near visually-lossless
   *  on typical phone-camera output). */
  quality?: number;
  /** Files smaller than this byte threshold are returned unchanged —
   *  modern phone photos (≈2–8 MB) are fine as-is, no point losing
   *  quality on a re-encode. Compression only kicks in above 10 MB.
   */
  minBytes?: number;
}

export interface CompressResult {
  /** Returned file. `same` when no compression was needed; otherwise
   *  a new File with `.jpg` extension and `image/jpeg` type. */
  file: File;
  /** Original byte size. */
  originalSize: number;
  /** Output byte size (== originalSize when `same`). */
  compressedSize: number;
  /** True when the original file was returned because compression
   *  would not have saved space, was unsupported, or the file was
   *  below `minBytes`. */
  same: boolean;
}

const SUPPORTED_TYPE_RE = /^image\/(jpe?g|png)$/i;

/**
 * Compress a single image File. Always resolves — failures fall back
 * to the original file so the caller doesn't have to special-case
 * exceptions in bulk-upload flows.
 */
export async function compressImage(
  file: File,
  opts: CompressOptions = {},
): Promise<CompressResult> {
  const { maxDim = 2560, quality = 0.92, minBytes = 10 * 1024 * 1024 } = opts;
  const originalSize = file.size;

  if (!SUPPORTED_TYPE_RE.test(file.type) || file.size <= minBytes) {
    return { file, originalSize, compressedSize: originalSize, same: true };
  }

  let img: HTMLImageElement;
  try {
    img = await loadImage(file);
  } catch {
    return { file, originalSize, compressedSize: originalSize, same: true };
  }

  const ratio = Math.min(
    1,
    maxDim / Math.max(img.naturalWidth, img.naturalHeight),
  );
  const w = Math.round(img.naturalWidth * ratio);
  const h = Math.round(img.naturalHeight * ratio);

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return { file, originalSize, compressedSize: originalSize, same: true };
  }
  ctx.drawImage(img, 0, 0, w, h);

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", quality),
  );

  if (!blob || blob.size >= originalSize) {
    return { file, originalSize, compressedSize: originalSize, same: true };
  }

  const baseName = file.name.replace(/\.(jpe?g|png)$/i, "");
  const out = new File([blob], `${baseName}.jpg`, {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
  return {
    file: out,
    originalSize,
    compressedSize: blob.size,
    same: false,
  };
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}

/** Render bytes as "1.23 MB" / "456 KB". Used in upload UI hints. */
export const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};
